<template>
  <div>
    <NavDark/>
      <section
    class="bg-cover gray"
    :style="{ backgroundImage:`url(${artistInfo?.artistBanner})`, backgroundRepeat:'no-repeat' }"
    aria-label="Kunstner præsentation"
    role="region"
  >
    <div class="container">
      <div class="row align-items-center justify-content-center">
        <div class="col-xl-12 col-lg-12 col-md-12 col-sm-12">
          <div class="text-center py-5 mt-3 mb-3">
            <div v-if="!artistInfo" class="col-12 text-center py-5" aria-live="polite" aria-busy="true">
              <div class="spinner-border text-dark" role="status" style="width: 3rem; height: 3rem;">
                <span class="visually-hidden">Indlæser…</span>
              </div>
            </div>

            <div v-else class="bg-white bg-opacity-90 rounded p-4 d-inline-block border">
              <div class="d-flex flex-column align-items-center gap-3">
                <img
                  v-if="artistInfo?.logo"
                  :src="artistInfo.logo"
                  :alt="(artistInfo?.name ? artistInfo.name + ' logo' : 'Kunstner logo')"
                  style="max-height: 200px; max-width: 300px;"
                  loading="lazy"
                  decoding="async"
                  fetchpriority="low"
                >
                <h1 class="ft-medium mb-1" v-html="artistInfo?.name || 'Henter kunstner'"></h1>
                <p class="mb-0" style="max-width:740px" v-html="artistInfo?.description"></p>

                <img
                  v-if="artistInfo?.image"
                  :src="artistInfo.image"
                  :alt="artistInfo?.artistPersonName ? ('Portræt af ' + artistInfo.artistPersonName) : 'Portræt af kunstner'"
                  class="rounded-circle border border-3 border-white"
                  style="width: 60px; height: 60px; object-fit: cover;"
                  loading="lazy"
                  decoding="async"
                >
                <p v-html="artistInfo.artistPersonName"></p>
              </div>

              <a
                v-if="artistInfo?.artistExternalUrl"
                :href="artistInfo.artistExternalUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn-dark"
                :aria-label="'Besøg eksternt website: ' + artistInfo.artistExternalUrl.replace(/^https?:\/\//, '')"
                :title="'Åbn ' + artistInfo.artistExternalUrl.replace(/^https?:\/\//, '') + ' i ny fane'"
              >
                Gå til {{ artistInfo.artistExternalUrl.replace(/^https?:\/\//, '') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <FilterOne @sort-change="handleSortChange" aria-label="Filtrering og sortering" />

  <section class="middle" v-if="artistInfo" aria-label="Kunstnerens produkter">
    <div class="container">
      <ProductGrid :products="displayedProducts" :loading="isLoading" />
    </div>
  </section>

  <FooterTopOne/>
  <FooterDark/>
  <ScrollToTop/>
  </div>
</template>

<script setup>
import FilterOne from '@/components/filter/filter-one.vue'
import FooterDark from '@/components/footer/footer-dark.vue'
import FooterTopOne from '@/components/footer/footer-top-one.vue'
import NavDark from '@/components/navbar/nav-dark.vue'
import ScrollToTop from '@/components/scroll-to-top.vue'
import ProductGrid from '@/components/product/product-grid.vue'

import { useProductStore } from '@/stores/useProductStore'
import { storeToRefs } from 'pinia'
import {ref, computed, watch} from 'vue'
import { useRoute } from 'vue-router'

import { updateMeta } from '@/utils/meta.js'
import {registerProfileView} from "@/services/api";

const sortKey = ref('default')
const route = useRoute()
const store = useProductStore()

const { isLoading } = storeToRefs(store)

const artistSubdomainParam = computed(() => String(route.params.artistSubdomain ?? ''))

watch(artistSubdomainParam, async (sub) => {
  if (sub) {
    registerProfileView(sub);
    await store.loadProducts({
      mode: 'artist',
      params: { artist_subdomain: sub },
      append: false,
    })
  }
}, { immediate: true })

const allProducts = computed(() => Array.isArray(store.productData) ? store.productData : [])

const toTs = (d) => {
  if (!d) return 0
  const s = String(d).trim().replace(' ', 'T')
  const t1 = Date.parse(s)
  const t2 = Date.parse(s + 'Z')
  return Number.isFinite(t1) ? t1 : (Number.isFinite(t2) ? t2 : 0)
}
const getCreatedTs = (item) => toTs(item.createDate ?? item.created_at ?? item.create_date)

const artistInfo = computed(() => {
  const a = allProducts.value[0]
  if (!a) return null
  return {
    id: String(a.artistId ?? a.artist_id ?? ''),
    name: a.artistName ?? '',
    artistPersonName: a.artistPersonName ?? '',
    shortDescription: a.artistShortDescription ?? '',
    description: a.artistDescription ?? '',
    logo: a.artistLogo ?? a.artistImage ?? '',
    image: a.artistImage ?? a.artistLogo ?? '',
    artistExternalUrl: a.artistExternalUrl ? new URL(a.artistExternalUrl).origin : null,
    artistBanner: a.artistBanner ?? ''
  }
})

watch([artistInfo, isLoading], ([newInfo, loading]) => {
  if (loading) {
    return;
  }

  if (newInfo && newInfo.id) {
    updateMeta(newInfo.name, newInfo.shortDescription);
  }
});

const displayedProducts = computed(() => {
  const copy = [...allProducts.value]
  switch (sortKey.value) {
    case 'price-asc':
      return copy.sort((a, b) => (a?.price ?? 0) - (b?.price ?? 0))
    case 'price-desc':
      return copy.sort((a, b) => (b?.price ?? 0) - (a?.price ?? 0))
    case 'new':
      return copy.sort((a, b) => getCreatedTs(b) - getCreatedTs(a))
    default:
      return copy
  }
})


const handleSortChange = (key) => {
  sortKey.value = key || 'default'
}
</script>